import { queryOptions } from "@tanstack/react-query"
import { fetchQuery, graphql } from "relay-runtime"
import BigNumber from "bignumber.js"

import type CosmosClient from "@perps/sdk/client/CosmosClient"
import type { TargetInfo } from "@future/target/info"
import { AppError } from "@future/libs/error/AppError"
import { SECOND } from "@common/utils/date"
import { useRujiraQueryLoaderStore } from "@future/rujira/RujiraQueryLoader"
import { Quote } from "@future/numerics"
import type { FullMarketInfo } from "@perps/sdk/client/CosmosClient"

import type { marketsQuery } from "./__generated__/marketsQuery.graphql"

const query = graphql`
  query marketsQuery {
    thorchainV2 {
      pools {
        asset {
          asset
        }
        assetTorPrice
      }
      rune {
        asset
        price {
          current
        }
      }
    }
  }
`

export const marketsQueryOptions = (
  chainClient: CosmosClient,
  targetInfo: TargetInfo,
) => {
  return queryOptions({
    queryKey: ["markets", targetInfo],
    refetchInterval: SECOND * 3,
    refetchIntervalInBackground: true,
    queryFn: async (context) => {
      try {
        const markets = await chainClient.queryMarkets(
          targetInfo,
          context.signal,
          true,
        )

        const environment = useRujiraQueryLoaderStore.getState().environment

        if (environment) {
          const observable = fetchQuery<marketsQuery>(environment, query, {})
          const data = await observable.toPromise()

          if (data?.thorchainV2) {
            const rujiraMarkets = markets.values().reduce((acc, market) => {
              acc.set(market.config.rujiraId, market)
              return acc
            }, new Map<string, FullMarketInfo>())

            if (data.thorchainV2.pools) {
              for (const pool of data.thorchainV2.pools) {
                const market = rujiraMarkets.get(pool.asset.asset)

                if (market) {
                  const num = new BigNumber(pool.assetTorPrice ?? 0)
                    .shiftedBy(-12)
                    .toNumber()
                  market.price.priceBase = new Quote(num)
                }
              }
            }

            if (data.thorchainV2.rune) {
              const market = rujiraMarkets.get(data.thorchainV2.rune.asset)

              if (market) {
                const num = new BigNumber(
                  data.thorchainV2.rune.price?.current ?? 0,
                )
                  .shiftedBy(-12)
                  .toNumber()
                market.price.priceBase = new Quote(num)
              }
            }
          }
        }

        return markets
      } catch (error) {
        throw AppError.fromText(
          `Could not load market configs for ${targetInfo.id}`,
          { cause: error },
        )
      }
    },
  })
}
